import { actualSite, BRAND_NAMES, formatName } from "@/app/utils/utils";
import { useState, FormEvent } from "react";
import { PatternFormat } from "react-number-format";
import chatbotIcon from "../../../../public/icon_whatsapp_chatboot.svg";
import type { FormData } from "./flows/types";
import { useRouter } from "next/router";
import { sendToSyonet } from "@/app/services/syonetIntegration";
import { normalizeModelName } from "@/app/services/handleSubmit";
import { pushDataLayerEvent } from "@/app/utils/dataLayer";
import type { FormPayload } from "@/app/interface/components";
import { FaTimes } from "react-icons/fa";


const AVAILABLE_CARS = ["COROLLA", "COROLLA CROSS", "HILUX", "SW4", "RAV4", "YARIS CROSS", "HIACE"];

export default function ChatBot() {
	const router = useRouter();
	const { lojas } = actualSite();
	const [isOpen, setIsOpen] = useState(false);
	const [formData, setFormData] = useState<FormData>({
		name: "",
		phone: "",
		email: "",
		cpf: "",
		model: "",
		dealer: "",
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formSubmitted, setFormSubmitted] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	// Adicionar função auxiliar para determinar category e product title
	const determinePageInfo = (pathname: string) => {
		// Remove a primeira barra e divide o caminho
		const pathParts = pathname.slice(1).split("/");

		if (pathParts[0] === "venda-direta") {
			return {
				categoryTitle: `Venda Direta - ${
					pathParts[1].charAt(0).toUpperCase() + pathParts[1].slice(1)
				}`,
				productTitle: "Sem informação - Lead Whatsapp",
			};
		}

		if (pathParts[0] === "servicos") {
			return {
				categoryTitle: `Serviços - ${
					pathParts[1].charAt(0).toUpperCase() + pathParts[1].slice(1)
				}`,
				productTitle: "Sem informação - Lead Whatsapp",
			};
		}

		// Para a home
		if (pathname === "/") {
			return {
				categoryTitle: "Veiculos Novos",
				productTitle: formData.model || "HOME",
			};
		}

		if (pathname === "/ofertas") {
			return {
				categoryTitle: "Veiculos Novos",
				productTitle: formData.model || "OFERTAS",
			};
		}

		// Para páginas de carros específicos
		if (
			pathParts.length === 1 &&
			!pathParts[0].includes("servicos") &&
			!pathParts[0].includes("venda-direta")
		) {
			const carro = router.query.carro as string;
			const modeloQuery = router.query.model as string;
			const nomeCarro = carro
				? carro.toUpperCase()
				: pathname.slice(1).toUpperCase();
			return {
				categoryTitle: "Veiculos Novos",
				productTitle: modeloQuery
					? `${nomeCarro} ${modeloQuery.toUpperCase()}`
					: nomeCarro,
			};
		}

		// Caso padrão
		return {
			categoryTitle: "Veiculos Novos",
			productTitle: pathname.slice(1).toUpperCase(),
		};
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setFormError(null);
		setIsSubmitting(true);

		try {
			const name = (formData.name || "").trim();
			const email = (formData.email || "").trim();
			const phone = (formData.phone || "").replace(/\D/g, "");
			const cpf = (formData.cpf || "").replace(/\D/g, "");
			const model = (formData.model || "").trim();
			const dealer = (formData.dealer || "").trim();

			const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ]+ [A-Za-zÀ-ÖØ-öø-ÿ ]+$/;
			if (!nameRegex.test(name)) {
				setFormError("Por favor, digite seu nome completo (nome e sobrenome).");
				return;
			}
			if (phone.length !== 11) {
				setFormError("Telefone inválido. Informe DDD + número (11 dígitos).");
				return;
			}
			if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
				setFormError("Digite um email válido.");
				return;
			}
			if (cpf.length !== 11) {
				setFormError("CPF inválido. Informe 11 dígitos.");
				return;
			}
			if (!model) {
				setFormError("Selecione o modelo.");
				return;
			}
			if (!dealer) {
				setFormError("Selecione a unidade.");
				return;
			}

			// Captura UTM params
			const urlParams = new URLSearchParams(window.location.search);
			const utmParams = urlParams.toString();

			const carro = router.query.carro as string;
			const modeloQuery = router.query.model as string;

			// Sempre usa o modelo do select como base para o payload
			let modelValue = model;
			if (router.pathname !== "/" && router.pathname !== "/ofertas") {
				// Mantém compatibilidade com páginas específicas (se você preferir priorizar URL)
				const nomeCarro = carro
					? carro.toUpperCase()
					: router.pathname.slice(1).toUpperCase();
				modelValue = modeloQuery
					? `${nomeCarro} ${modeloQuery.toUpperCase()}`
					: nomeCarro;
			}

			const pageValue =
				router.pathname === "/"
					? "HOME"
					: carro
						? carro.toUpperCase()
						: router.pathname.slice(1).toUpperCase();

			const { categoryTitle, productTitle } = determinePageInfo(router.pathname);

			const requestBody = {
				name,
				email,
				cpf,
				phone,
				params: utmParams,
				consorcio: false,
				registrationNumber: cpf,
				team: BRAND_NAMES.ALL_CAPS,
				locationId: 1,
				model: modelValue,
				page: pageValue,
				wpp: true,
				chatbot: router.pathname === "/" ? "HOME" : pageValue,
				categoryId: 1,
				brandId: 67260,
				dealer: dealer || "Whatsapp",
				contactMethod: "WHATSAPP",
				categoryTitle,
				productTitle,
				service: router.pathname.includes("/servicos/pos-venda")
					? "Atendimento Pós-Venda"
					: undefined,
			};

			const response = await fetch("/api/submitForm", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify(requestBody),
			});

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`Erro ao enviar formulário: ${response.status} ${errorText}`);
			}

			pushDataLayerEvent("lead_whatsapp");

			// Envia para Syonet após sucesso no envio do formulário
			try {
				let categoria = "0 Km";
				if (router.pathname.includes("/servicos")) categoria = "Serviços";
				else if (router.pathname.includes("venda-direta")) categoria = "Venda Direta";

				const normalizedModel = normalizeModelName(model);

				const syonetPayload: FormPayload & { categoria?: string } = {
					name,
					email,
					cellPhone: phone,
					registrationNumber: cpf,
					consorcio: false,
					team: BRAND_NAMES.ALL_CAPS,
					locationId: 1,
					model: normalizedModel,
					page: pageValue,
					wpp: true,
					dealer: dealer || "Whatsapp",
					comment: `Chatbot - ${router.pathname === "/" ? "HOME" : pageValue}`,
					categoria,
				};

				await sendToSyonet(syonetPayload, "toyota", {
					skipToyotaLeadCenter: true,
				}).catch(() => {});
			} catch {
				// não bloqueia o fluxo
			}

			setFormSubmitted(true);
		} catch (error) {
			setFormError(
				error instanceof Error
					? error.message
					: "❌ Ocorreu um erro ao processar sua solicitação."
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const inputClassName =
		"w-full p-3 border-b-2 border-gray-400 focus:outline-none focus:ring-2 focus:ring-grey-600 focus:border-transparent transition-all";

	const inputSelectClassName =
		"w-full p-2 rounded border-2 border-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all";	

	return (
		<>
			<section className="flex flex-col fixed z-50 lg:bottom-10 lg:right-10 bottom-[calc(env(safe-area-inset-bottom)+20px)] right-5 w-[60px] h-[60px]">
				{isOpen ? (
					<>
						<main className="absolute lg:bottom-0 lg:right-0 flex flex-col overflow-hidden lg:rounded-md lg:w-[360px] lg:h-[490px] max-lg:w-screen max-lg:h-[100dvh] max-lg:fixed max-lg:top-0 max-lg:left-0 max-lg:right-0 max-lg:bottom-0 max-lg:z-40 shadow-[3px_4px_12px_rgba(0,0,0,0.15)] bg-white">
							<div className="flex-1 overflow-y-auto px-5 py-4">
								<header className="flex items-center justify-between gap-3">
									<h1 className="text-base text-black">
										Atendimento via WhatsApp
									</h1>
									<button
										type="button"
										onClick={() => setIsOpen(false)}
										className="p-1 text-gray-500 hover:text-gray-700 rounded"
										aria-label="Fechar"
									>
										<FaTimes className="w-5 h-5" />
									</button>
								</header>
								<div className="h-[2px] w-1/4 bg-red-500 mb-2" />

								{formError && (
									<div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
										{formError}
									</div>
								)}

								{formSubmitted ? (
									<div className="p-4 rounded border border-green-200 bg-green-50 text-green-700 text-sm">
										✅ Dados enviados com sucesso! Abrindo WhatsApp...
									</div>
								) : (
									<form onSubmit={handleSubmit} className="flex flex-col gap-3 text-black">
										<div>
										
											<input
												className={inputClassName}
												value={formData.name}
												onChange={(e) =>
													setFormData((prev) => ({ ...prev, name: e.target.value }))
												}
												placeholder="Nome*"
											/>
										</div>

										<div>
										
											<input
												type="email"
												className={inputClassName}
												value={formData.email}
												onChange={(e) =>
													setFormData((prev) => ({ ...prev, email: e.target.value }))
												}
												placeholder="E-mail*"
											/>
										</div>

										<div>
											
											<PatternFormat
												value={formData.phone}
												onValueChange={({ value }) =>
													setFormData((prev) => ({ ...prev, phone: value }))
												}
												format="(##) #####-####"
												mask="_"
												className={inputClassName}
												placeholder="Telefone*"
											/>
										</div>

										<div>
										
											<PatternFormat
												value={formData.cpf}
												onValueChange={({ value }) =>
													setFormData((prev) => ({ ...prev, cpf: value }))
												}
												format="###.###.###-##"
												mask="_"
												className={inputClassName}
												placeholder="CPF*"
											/>
										</div>

										<div>
									
											<select
												className={inputSelectClassName}
												value={formData.model || ""}
												onChange={(e) =>
													setFormData((prev) => ({ ...prev, model: e.target.value }))
												}
											>
												<option value="">Selecione o modelo</option>
												{AVAILABLE_CARS.map((car) => (
													<option key={car} value={car}>
														{car}
													</option>
												))}
											</select>
										</div>

										<div>
											
											<select
												className={inputSelectClassName}
												value={formData.dealer || ""}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														dealer: e.target.value,
													}))
												}
											>
												<option value="">Selecione a unidade</option>
												{lojas.map((loja) => (
													<option key={loja.nome} value={loja.nome}>
														{formatName(loja.nome)}
													</option>
												))}
											</select>
										</div>

										<button
											type="submit"
											disabled={isSubmitting}
											className={`mt-2 px-4 py-2 rounded w-full transition-colors ${
												isSubmitting
													? "bg-gray-400 cursor-not-allowed"
													: "bg-red-600 hover:bg-red-700"
											} text-white font-semibold`}
										>
											{isSubmitting ? "Enviando..." : "Enviar e ir para WhatsApp"}
										</button>
									</form>
								)}
							</div>
						</main>
					</>
				) : (
					<>
						{!router.pathname.includes("black-week") && (
							<img
								loading="lazy"
								src={chatbotIcon.src}
								alt="Assistente virtual"
								className="object-contain w-[60px] h-[60px] cursor-pointer hover:scale-105 transition-transform"
								onClick={() => setIsOpen(true)}
							/>
						)}
					</>
				)}
			</section>
		</>
	);
}
